import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Receipt, CheckCircle2, Clock, XCircle } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

const STATUS_PILL = {
  paid: { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { Icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  expired: { Icon: XCircle, cls: 'bg-red-50 text-red-700 border-red-200' },
};

export default function Invoices() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/academy/my/invoices')
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">Invoices</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Your payment history with ZHELTA.</p>

        {loading ? (
          <div className="text-[#6E6E73] text-[14px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center">
            <Receipt className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
            <div className="text-[16px] font-bold text-[#1D1D1F]">No invoices yet</div>
            <p className="text-[13.5px] text-[#6E6E73]">Invoices appear after a successful enrollment payment.</p>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white">
            <table className="w-full text-[14px]">
              <thead className="bg-[#0e0e10] text-white">
                <tr>
                  <th className="px-5 py-3 text-left text-[11px] font-bold tracking-[0.18em] uppercase">Date</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold tracking-[0.18em] uppercase">Item</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold tracking-[0.18em] uppercase">Tier</th>
                  <th className="px-5 py-3 text-right text-[11px] font-bold tracking-[0.18em] uppercase">Amount</th>
                  <th className="px-5 py-3 text-left text-[11px] font-bold tracking-[0.18em] uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E7]">
                {rows.map((r, i) => {
                  const ps = (r.payment_status || 'pending').toLowerCase();
                  const meta = STATUS_PILL[ps] || STATUS_PILL.pending;
                  const Icon = meta.Icon;
                  return (
                    <tr key={r.session_id || i} data-testid={`invoice-row-${i}`}>
                      <td className="px-5 py-3 text-[#6E6E73]">{(r.created_at || '').slice(0, 10)}</td>
                      <td className="px-5 py-3 font-bold text-[#1D1D1F]">{r.service_name || r.slug}</td>
                      <td className="px-5 py-3 text-[#6E6E73]">{r.tier_name || r.tier_id}</td>
                      <td className="px-5 py-3 text-right font-bold text-[#1D1D1F]">${Number(r.amount || 0).toFixed(2)} {r.currency?.toUpperCase()}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                          <Icon className="w-3 h-3" /> {ps}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
